export const GET_ALL_VENUES = "venues/all";
export const GET_SEARCH_VENUES = (key) =>
  `venues/search/${encodeURIComponent(String(key ?? "").trim())}`;
export const GET_ALL_VENUES_SHORT = (sortcolumn) => `venues/all/${sortcolumn}`;
export const GET_VENUE = (venueId) => `venues/${venueId}`;
export const DELETE_VENUE = (venueId) => `venues/delete/${venueId}`;
export const EDIT_VENUE = "venues/update";
export const CREATE_VENUE = "venues/create";

export const SET_LOGIN = "auth/login/admin";

export const DELETE_TEAM = (teamId) => `admin/teams/delete/${teamId}`;
export const EDIT_TEAM = "admin/teams/update";
export const CREATE_TEAM = "admin/teams/create";

export const GET_ALL_GOVERNING_BODIES = "governingbody/all";
export const GET_CONFERENCES = "conferences/all";
export const GET_ALL_COUNTRIES_ISO = "country/all";
export const GET_STATES_BY_COUNTRY_ISO = (country) => `state/${country}`;

export const GET_ALL_ADS = "admin/ads/all";
export const DELETE_AD = (adId) => `admin/ads/delete/${adId}`;
export const CREATE_AD = "admin/ads/create";
export const EDIT_AD = "admin/ads/update";

// Optional but recommended: explicit pause/resume so the backend stays narrow.
export const SET_AD_STATUS = (adId) => `admin/ads/${adId}/status`;

// Builds "?a=1&b=2" from an object; undefined/null/blank values are omitted.
const buildQueryString = (params) => {
  const usp = new URLSearchParams();
  Object.entries(params || {}).forEach(([key, value]) => {
    if (value === undefined || value === null) return;
    if (typeof value === "string" && value.trim() === "") return;
    usp.append(key, String(value));
  });
  const qs = usp.toString();
  return qs ? `?${qs}` : "";
};

// Governing body list filters (status, teamType, locale). The backend treats a
// missing param as "no filter", so "ALL" is dropped here rather than sent.
// Locale is an IETF tag with a hyphen ("en-US"); the backend matches it exactly.
const buildGoverningBodyListQuery = ({ status, teamType, locale } = {}) => {
  const normalize = (value) => {
    const trimmed = String(value ?? "").trim().toUpperCase();
    return trimmed === "" || trimmed === "ALL" ? undefined : trimmed;
  };
  return buildQueryString({
    status: normalize(status),
    teamType: normalize(teamType),
    locale: String(locale ?? "").trim() || undefined,
  });
};

export const GET_GOVERNING_BODIES_ADMIN_ALL = ({
  page = 0,
  limit = 20,
  sort = "Name",
  isAscending = true,
  status,
  teamType,
  locale,
} = {}) => {
  const qs = buildGoverningBodyListQuery({ status, teamType, locale });
  return `admin/governing-bodies/all/${page}/${limit}/${sort}/${isAscending}${qs}`;
};

export const GET_GOVERNING_BODIES_ADMIN_SEARCH = ({
  key = "",
  page = 0,
  limit = 20,
  sort = "Name",
  isAscending = true,
  status,
  teamType,
  locale,
} = {}) => {
  const safeKey = encodeURIComponent(String(key || "").trim());
  const qs = buildGoverningBodyListQuery({ status, teamType, locale });
  return `admin/governing-bodies/search/${safeKey}/${page}/${limit}/${sort}/${isAscending}${qs}`;
};

export const SAVE_GOVERNING_BODY_ADMIN = "admin/governing-bodies";
export const GET_DD_LISTS = "admin/dd-lists";
export const GET_POINT_SCHEMAS = "pointSchema/all";
export const GET_RULE_GROUPS = "rules/groups";

export const GET_TEAM = (teamId) => `admin/teams/${encodeURIComponent(teamId)}`;

// One page of teams with no search key. The search endpoint can't do this: a blank key
// becomes LIKE '% %', which only matches names containing a space.
export const LIST_TEAMS = ({
  page = 0,
  limit = 25,
  sort = "ID",
  isAscending = true,
  teamType,
} = {}) =>
  `admin/teams/all/${page}/${limit}/${sort}/${isAscending}${buildQueryString({ teamType })}`;

// Team search by name. `teamType` is optional; omit it to search every team type.
export const SEARCH_TEAMS = ({
  query = "all",
  page = 0,
  limit = 25,
  sort = "ID",
  isAscending = true,
  teamType,
} = {}) => {
  const safeQuery = encodeURIComponent(query || "all");
  return `admin/teams/search/${safeQuery}/${page}/${limit}/${sort}/${isAscending}${buildQueryString(
    { teamType },
  )}`;
};

export const SEARCH_GOVERNING_BODY_TEAMS = (params = {}) =>
  SEARCH_TEAMS({ ...params, teamType: "G" });

// Qualifying scores admin (query-string params, not path segments).
// Shared timezone list. Both params optional and additive; unknown values fail open
// (unknown state -> country list, unknown country -> full list), never an empty list.
export const GET_TIMEZONES = ({ country, state } = {}) =>
  `timezones${buildQueryString({ country, state })}`;

export const QUALIFYING_SCORES_BASE = "admin/qualifying-scores";

export const GET_QUALIFYING_SCORES = (params) =>
  `${QUALIFYING_SCORES_BASE}${buildQueryString(params)}`;

export const GET_QUALIFYING_SCORE_BY_ID = (id) =>
  `${QUALIFYING_SCORES_BASE}/${encodeURIComponent(id)}`;

export const SAVE_QUALIFYING_SCORE = QUALIFYING_SCORES_BASE;

export const DELETE_QUALIFYING_SCORE = (id) =>
  `${QUALIFYING_SCORES_BASE}/${encodeURIComponent(id)}`;

export const CLONE_QUALIFYING_SCORES = `${QUALIFYING_SCORES_BASE}/clone`;

export const EXPORT_QUALIFYING_SCORES = (format, params) =>
  `${QUALIFYING_SCORES_BASE}/export.${format}${buildQueryString(params)}`;

// Division/Region suggestions in use for one governing body.
export const GET_QUALIFYING_SCORE_FILTER_VALUES = (governingBodyId) =>
  `${QUALIFYING_SCORES_BASE}/filter-values${buildQueryString({
    governingBodyId,
  })}`;

// QUALIFYING_SCORES templates a governing body's scores may use. `includeId` adds a score's
// current template even when it is inactive, so the picker can still show it.
export const GET_QUALIFYING_SCORE_TEMPLATES = (governingBodyId, includeId) =>
  `admin/governing-bodies/${encodeURIComponent(
    governingBodyId,
  )}/qualifying-score-templates${buildQueryString({ includeId })}`;

// Google Maps autofill (system administrator only; backend persists nothing).
export const MAPS_BASE = "admin/maps";

export const MAPS_RESOLVE = `${MAPS_BASE}/resolve`;

export const MAPS_LOGO_SEARCH = (params) =>
  `${MAPS_BASE}/logo/search${buildQueryString(params)}`;

export const MAPS_LOGO_IMPORT = `${MAPS_BASE}/logo/import`;
